import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { mapOrderStatusForDisplay } from '../utils/orderStatusDisplay';

interface Image {
  id: string;
  filename: string;
  category: string;
  alt?: string;
  description?: string;
  width?: number;
  height?: number;
  size: number;
  isActive: boolean;
  uploadedAt: string;
  url: string;
  thumbnailUrl: string;
}

interface BrochureRequest {
  id: string;
  name: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  status: string;
  notes?: string;
  requestedAt: string;
  fulfilledAt?: string;
}

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [currentUser, setCurrentUser] = useState<{ email: string; fullName?: string } | null>(null);
  
  const [activeTab, setActiveTab] = useState<'images' | 'brochures' | 'products' | 'pricing-plans' | 'accessories' | 'abandoned-carts' | 'orders' | 'users' | 'access-requests' | 'email-test' | 'newsletter'>('images');
  const [images, setImages] = useState<Image[]>([]);
  const [brochureRequests, setBrochureRequests] = useState<BrochureRequest[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [pricingPlans, setPricingPlans] = useState<any[]>([]);
  const [accessories, setAccessories] = useState<any[]>([]);
  const [abandonedCarts, setAbandonedCarts] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [accessRequests, setAccessRequests] = useState<any[]>([]);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [newUserForm, setNewUserForm] = useState({ email: '', password: '', fullName: '', role: 'admin' });
  const [reviewingRequest, setReviewingRequest] = useState<any | null>(null);
  const [reviewPassword, setReviewPassword] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [editUserForm, setEditUserForm] = useState({ fullName: '', role: '', isActive: true });
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalCallback, setPasswordModalCallback] = useState<(() => void) | null>(null);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(false);
  const [showReviewPassword, setShowReviewPassword] = useState(false);
  const [showProfileCurrentPassword, setShowProfileCurrentPassword] = useState(false);
  const [showProfileNewPassword, setShowProfileNewPassword] = useState(false);
  const [showProfileConfirmPassword, setShowProfileConfirmPassword] = useState(false);
  const [detailsModalContent, setDetailsModalContent] = useState<{ title: string; json: string } | null>(null);
  const [detailsModalCopied, setDetailsModalCopied] = useState(false);
  const [ordersReportSending, setOrdersReportSending] = useState(false);
  const [abandonedCartsReportSending, setAbandonedCartsReportSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('general');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [brochureStatusFilter, setBrochureStatusFilter] = useState<string>('all');
  const [abandonedCartFilter, setAbandonedCartFilter] = useState<'all' | 'identified' | 'anonymous'>('all');
  const [abandonedCartStats, setAbandonedCartStats] = useState<{ total: number; anonymousCount: number; identifiedCount: number } | null>(null);
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'name-asc' | 'name-desc' | 'size-desc' | 'size-asc'>('date-desc');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteText, setNoteText] = useState<string>('');
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [editingFilename, setEditingFilename] = useState<Record<string, string>>({});
  const [filenameError, setFilenameError] = useState<Record<string, string>>({});

  // Email Test tab (superuser only)
  const [emailTestSendTo, setEmailTestSendTo] = useState('');
  const [emailTestSending, setEmailTestSending] = useState(false);

  // Newsletter tab (superuser only)
  const [newsletterListSource, setNewsletterListSource] = useState<'system' | 'csv'>('system');
  const [newsletterHtml, setNewsletterHtml] = useState('');
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterFrom, setNewsletterFrom] = useState('');
  const [newsletterRecipientCount, setNewsletterRecipientCount] = useState<number | null>(null);
  const [newsletterWordFile, setNewsletterWordFile] = useState<File | null>(null);
  const [newsletterCsvData, setNewsletterCsvData] = useState('');
  const [newsletterConfig, setNewsletterConfig] = useState<{ configured: boolean; defaultFrom: string } | null>(null);
  const [newsletterPreviewSamples, setNewsletterPreviewSamples] = useState<{ email: string; html: string }[] | null>(null);
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [newsletterBounceReportSending, setNewsletterBounceReportSending] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState<{
    success: boolean;
    configOk: boolean;
    sendOk: boolean;
    backend?: 'graph' | 'smtp';
    error?: string;
    message?: string;
  } | null>(null);
  
  // CRUD form states
  const [editingAccessory, setEditingAccessory] = useState<any | null>(null);
  const [isCreatingAccessory, setIsCreatingAccessory] = useState(false);
  const [accessoryForm, setAccessoryForm] = useState({
    name: '',
    description: '',
    price: 0,
    priceType: 'one-time',
    priceUnit: '',
    imageFilename: '',
    category: 'accessory',
    isActive: true,
    sortOrder: 0,
    technicalSpecs: '',
    additionalImages: '' as string // Comma-separated filenames
  });
  
  const [editingProduct, setEditingProduct] = useState<any | null>(null);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    shortName: '',
    description: '',
    imageFilename: '',
    category: 'in-home',
    basePrice: 0,
    sortOrder: 0
  });
  
  const [editingPlan, setEditingPlan] = useState<any | null>(null);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    productId: '',
    planType: 'annual',
    name: '',
    billingCycle: 'annual',
    monthlyPrice: 0,
    annualTotal: 0,
    setupFee: 0,
    activationFee: 0,
    sortOrder: 0
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const getAuthHeaders = () => {
    const adminEmail = sessionStorage.getItem('adminEmail');
    const adminPassword = sessionStorage.getItem('adminPassword');
    if (!adminEmail || !adminPassword) return null;
    return { 'x-admin-email': adminEmail, 'x-admin-password': adminPassword };
  };
  
  // Check if already logged in (session storage)
  useEffect(() => {
    const adminAuth = sessionStorage.getItem('adminAuth');
    const adminEmail = sessionStorage.getItem('adminEmail');
    const adminName = sessionStorage.getItem('adminName');
    const adminIsSuperUser = sessionStorage.getItem('adminIsSuperUser') === 'true';
    
    if (adminAuth === 'authenticated' && adminEmail) {
      setIsAuthenticated(true);
      setIsSuperUser(adminIsSuperUser);
      setCurrentUser({
        email: adminEmail,
        fullName: adminName || undefined,
      });
    }
  }, []);
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    
    if (!email || !password) {
      setLoginError('Email and password are required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success && data.user) {
        setIsAuthenticated(true);
        setIsSuperUser(data.user.isSuperUser === true);
        setUserRole(data.user.role || 'admin');
        setCurrentUser({
          email: data.user.email,
          fullName: data.user.fullName,
        });
        sessionStorage.setItem('adminAuth', 'authenticated');
        sessionStorage.setItem('adminEmail', data.user.email);
        sessionStorage.setItem('adminPassword', password); // Store password for session
        sessionStorage.setItem('adminIsSuperUser', String(data.user.isSuperUser === true));
        sessionStorage.setItem('adminRole', data.user.role || 'admin');
        if (data.user.fullName) {
          sessionStorage.setItem('adminName', data.user.fullName);
        }
        setLoginError('');
        setEmail('');
        setPassword('');
      } else {
        setLoginError(data.error || 'Invalid email or password');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setLoginError('Login failed. Please try again.');
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setUserRole('');
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminEmail');
    sessionStorage.removeItem('adminPassword');
    sessionStorage.removeItem('adminName');
    sessionStorage.removeItem('adminIsSuperUser');
    sessionStorage.removeItem('adminRole');
  };

  const categories = [
    { value: 'logo', label: 'Logo' },
    { value: 'hero', label: 'Hero Section' },
    { value: 'product', label: 'Product Images' },
    { value: 'testimonial', label: 'Testimonials' },
    { value: 'general', label: 'General' },
  ];

  useEffect(() => {
    // Load user role from sessionStorage on mount
    const storedRole = sessionStorage.getItem('adminRole');
    if (storedRole) {
      setUserRole(storedRole);
    }

    if (activeTab === 'images') {
      fetchImages();
    } else if (activeTab === 'brochures') {
      fetchBrochureRequests();
    } else if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'pricing-plans') {
      fetchPricingPlans();
    } else if (activeTab === 'accessories') {
      fetchAccessories();
    } else if (activeTab === 'abandoned-carts') {
      fetchAbandonedCarts();
    } else if (activeTab === 'orders' && isSuperUser) {
      fetchOrders();
    } else if (activeTab === 'users' && isSuperUser) {
      fetchAdminUsers();
    } else if (activeTab === 'access-requests' && isSuperUser) {
      fetchAccessRequests();
    } else if (activeTab === 'newsletter' && isSuperUser) {
      fetchNewsletterConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterCategory, brochureStatusFilter, isSuperUser]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const response = await axios.get(`${API_URL}/api/images`, { params });
      setImages(response.data.images);
      setSelectedImages(new Set()); // Clear selections when refreshing
    } catch (error) {
      console.error('Error fetching images:', error);
      setError('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrochureRequests = async () => {
    const h = getAuthHeaders();
    if (!h) {
      setError('Session expired. Please log in again.');
      return;
    }
    try {
      setLoading(true);
      const params = brochureStatusFilter !== 'all' ? { status: brochureStatusFilter } : {};
      const response = await axios.get(`${API_URL}/api/brochure-requests`, { params, headers: h });
      setBrochureRequests(response.data.requests);
    } catch (error) {
      console.error('Error fetching brochure requests:', error);
      setError('Failed to fetch brochure requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products`);
      // Extract all pricing plans from all products
      const allPlans = response.data.products.flatMap((p: any) => 
        p.pricingPlans.map((plan: any) => ({
          ...plan,
          productName: p.name
        }))
      );
      setPricingPlans(allPlans);
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      setError('Failed to fetch pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/accessories`);
      setAccessories(response.data.accessories);
    } catch (error) {
      console.error('Error fetching accessories:', error);
      setError('Failed to fetch accessories');
    } finally {
      setLoading(false);
    }
  };

  const fetchAbandonedCarts = async (filter?: 'all' | 'identified' | 'anonymous') => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      setError('Please log in again to view abandoned carts');
      return;
    }
    const f = filter ?? abandonedCartFilter;
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (f === 'identified') params.anonymous = 'false';
      if (f === 'anonymous') params.anonymous = 'true';
      const response = await axios.get(`${API_URL}/api/abandoned-carts`, { params, headers: authHeaders });
      setAbandonedCarts(response.data.carts || []);
      setAbandonedCartStats({
        total: response.data.total ?? 0,
        anonymousCount: response.data.anonymousCount ?? 0,
        identifiedCount: response.data.identifiedCount ?? 0,
      });
    } catch (error) {
      console.error('Error fetching abandoned carts:', error);
      setError('Failed to fetch abandoned carts');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      setError('Please log in again to view orders');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/orders`, { params: { limit: 100 }, headers: authHeaders });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const generateOrdersReport = async () => {
    const adminEmail = sessionStorage.getItem('adminEmail');
    const adminPassword = sessionStorage.getItem('adminPassword');
    if (!adminEmail || !adminPassword) {
      setError('Please log in again to generate the report');
      return;
    }
    try {
      setOrdersReportSending(true);
      setError('');
      setSuccess('');
      const response = await axios.post(`${API_URL}/api/orders/daily-report`, {}, {
        headers: {
          'x-admin-email': adminEmail,
          'x-admin-password': adminPassword,
        },
      });
      if (response.data?.ok) {
        const label = response.data.dateLabel ? ` for ${response.data.dateLabel}` : '';
        const count = typeof response.data.orderCount === 'number' ? ` (${response.data.orderCount} order(s))` : '';
        setSuccess(`Orders daily report sent${label}${count}. Check inbox for encrypted CSV + password email.`);
      } else {
        setError(response.data?.message || response.data?.error || 'Report generation failed');
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to generate report';
      setError(msg);
    } finally {
      setOrdersReportSending(false);
    }
  };

  const generateAbandonedCartsReport = async () => {
    const adminEmail = sessionStorage.getItem('adminEmail');
    const adminPassword = sessionStorage.getItem('adminPassword');
    if (!adminEmail || !adminPassword) {
      setError('Please log in again to generate the report');
      return;
    }
    try {
      setAbandonedCartsReportSending(true);
      setError('');
      setSuccess('');
      const response = await axios.post(`${API_URL}/api/abandoned-carts/daily-report`, {}, {
        headers: {
          'x-admin-email': adminEmail,
          'x-admin-password': adminPassword,
        },
      });
      if (response.data?.ok) {
        const label = response.data.dateLabel ? ` for ${response.data.dateLabel}` : '';
        const detail =
          typeof response.data.identifiedCount === 'number'
            ? ` (${response.data.anonymousCount ?? 0} anonymous count-only, ${response.data.identifiedCount} identified in CSV)`
            : '';
        setSuccess(`Abandoned carts daily report sent${label}${detail}.`);
      } else {
        setError(response.data?.message || response.data?.error || 'Report generation failed');
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to generate report';
      setError(msg);
    } finally {
      setAbandonedCartsReportSending(false);
    }
  };

  const fetchAdminUsers = async (password?: string) => {
    try {
      setLoading(true);
      const adminEmail = sessionStorage.getItem('adminEmail');
      const adminPassword = password || sessionStorage.getItem('adminPassword') || '';
      
      if (!adminPassword) {
        setPasswordModalCallback(() => () => fetchAdminUsers(passwordConfirm));
        setShowPasswordModal(true);
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}/api/admin-auth/users/list`, {
        email: adminEmail,
        password: adminPassword,
      });
      setAdminUsers(response.data.users || []);
      if (password) {
        sessionStorage.setItem('adminPassword', password);
      }
    } catch (error: any) {
      console.error('Error fetching admin users:', error);
      setError('Failed to fetch admin users. Super-user access required.');
    } finally {
      setLoading(false);
      setShowPasswordModal(false);
      setPasswordConfirm('');
      setShowPasswordConfirmModal(false);
    }
  };

  const fetchAccessRequests = async (password?: string) => {
    try {
      setLoading(true);
      const adminEmail = sessionStorage.getItem('adminEmail');
      const adminPassword = password || sessionStorage.getItem('adminPassword') || '';
      
      if (!adminPassword) {
        setPasswordModalCallback(() => () => fetchAccessRequests(passwordConfirm));
        setShowPasswordModal(true);
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}/api/admin-auth/access-requests`, {
        email: adminEmail,
        password: adminPassword,
      });
      setAccessRequests(response.data.requests || []);
      if (password) {
        sessionStorage.setItem('adminPassword', password);
      }
    } catch (error: any) {
      console.error('Error fetching access requests:', error);
      setError('Failed to fetch access requests. Super-user access required.');
    } finally {
      setLoading(false);
      setShowPasswordModal(false);
      setPasswordConfirm('');
      setShowPasswordConfirmModal(false);
    }
  };

  const handleCreateUser = async (password?: string) => {
    if (!newUserForm.email || !newUserForm.password) {
      setError('Email and password are required');
      return;
    }

    if (newUserForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const adminPassword = password || sessionStorage.getItem('adminPassword') || '';
    if (!adminPassword) {
      setPasswordModalCallback(() => () => handleCreateUser(passwordConfirm));
      setShowPasswordModal(true);
      return;
    }

    try {
      const adminEmail = sessionStorage.getItem('adminEmail');

      const response = await axios.post(`${API_URL}/api/admin-auth/users`, {
        email: newUserForm.email,
        password: newUserForm.password,
        fullName: newUserForm.fullName,
        role: newUserForm.role || 'admin',
        adminEmail,
        adminPassword,
      });

      if (response.data.success) {
        setSuccess('User created successfully!');
        setNewUserForm({ email: '', password: '', fullName: '', role: 'admin' });
        if (password) {
          sessionStorage.setItem('adminPassword', password);
        }
        fetchAdminUsers(adminPassword);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create user');
    } finally {
      setShowPasswordModal(false);
      setPasswordConfirm('');
      setShowPasswordConfirmModal(false);
    }
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setEditUserForm({
      fullName: user.fullName || '',
      role: user.role || 'admin',
      isActive: user.isActive !== false,
    });
  };

  const handleUpdateUser = async (password?: string) => {
    if (!editingUser) return;

    const adminPassword = password || sessionStorage.getItem('adminPassword') || '';
    if (!adminPassword) {
      setPasswordModalCallback(() => () => handleUpdateUser(passwordConfirm));
      setShowPasswordModal(true);
      return;
    }

    try {
      const adminEmail = sessionStorage.getItem('adminEmail');

      // Update user details
      const updateResponse = await axios.patch(
        `${API_URL}/api/admin-auth/users/${editingUser.id}`,
        {
          fullName: editUserForm.fullName || null,
          isActive: editUserForm.isActive,
        },
        {
          headers: {
            'x-admin-email': adminEmail,
            'x-admin-password': adminPassword,
          },
        }
      );

      // Update role if changed
      if (editUserForm.role !== editingUser.role) {
        await axios.post(
          `${API_URL}/api/admin-auth/users/${editingUser.id}/role`,
          { role: editUserForm.role },
          {
            headers: {
              'x-admin-email': adminEmail,
              'x-admin-password': adminPassword,
            },
          }
        );
      }

      if (updateResponse.data.success) {
        setSuccess('User updated successfully!');
        setEditingUser(null);
        if (password) {
          sessionStorage.setItem('adminPassword', password);
        }
        fetchAdminUsers(adminPassword);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update user');
    } finally {
      setShowPasswordModal(false);
      setPasswordConfirm('');
      setShowPasswordConfirmModal(false);
    }
  };

  const handleToggleUserActive = async (user: any, password?: string) => {
    const adminPassword = password || sessionStorage.getItem('adminPassword') || '';
    if (!adminPassword) {
      setPasswordModalCallback(() => () => handleToggleUserActive(user, passwordConfirm));
      setShowPasswordModal(true);
      return;
    }

    try {
      const adminEmail = sessionStorage.getItem('adminEmail');

      const response = await axios.patch(
        `${API_URL}/api/admin-auth/users/${user.id}`,
        { isActive: !user.isActive },
        {
          headers: {
            'x-admin-email': adminEmail,
            'x-admin-password': adminPassword,
          },
        }
      );

      if (response.data.success) {
        setSuccess(`User ${user.isActive ? 'deactivated' : 'activated'} successfully!`);
        if (password) {
          sessionStorage.setItem('adminPassword', password);
        }
        fetchAdminUsers(adminPassword);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update user status');
    } finally {
      setShowPasswordModal(false);
      setPasswordConfirm('');
      setShowPasswordConfirmModal(false);
    }
  };

  const handlePasswordModalSubmit = () => {
    if (!passwordConfirm) {
      setError('Password is required');
      return;
    }
    if (passwordModalCallback) {
      passwordModalCallback();
    }
  };

  const handleReviewRequest = async (requestId: string, action: 'approve' | 'reject') => {
    if (action === 'approve' && !reviewPassword) {
      setError('Password is required to approve access');
      return;
    }

    if (action === 'approve' && reviewPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const adminPassword = sessionStorage.getItem('adminPassword') || '';
    if (!adminPassword) {
      setPasswordModalCallback(() => () => handleReviewRequest(requestId, action));
      setShowPasswordModal(true);
      return;
    }

    try {
      const adminEmail = sessionStorage.getItem('adminEmail');
      
      if (!adminPassword) return;

      const response = await axios.post(`${API_URL}/api/admin-auth/access-requests/${requestId}/review`, {
        email: adminEmail,
        password: adminPassword,
        action,
        newPassword: reviewPassword, // New user's password (only for approve)
        notes: reviewNotes,
      });

      if (response.data.success) {
        setSuccess(`Access request ${action === 'approve' ? 'approved' : 'rejected'}!`);
        setReviewingRequest(null);
        setReviewPassword('');
        setReviewNotes('');
        setShowReviewPassword(false);
        fetchAccessRequests();
      }
    } catch (error: any) {
      setError(error.response?.data?.error || `Failed to ${action} request`);
    }
  };

  const fetchNewsletterConfig = async () => {
    const h = getAuthHeaders();
    if (!h) return;
    try {
      const res = await axios.get(`${API_URL}/api/newsletter/config`, { headers: h });
      setNewsletterConfig(res.data);
      setNewsletterFrom(res.data.defaultFrom || '');
    } catch (_) {
      setNewsletterConfig({ configured: false, defaultFrom: 'Alert1 Newsletter <newsletter@alert-1.com>' });
    }
  };

  const fetchNewsletterRecipients = async () => {
    const h = getAuthHeaders();
    if (!h) return;
    try {
      const res = await axios.get(`${API_URL}/api/newsletter/recipients?source=system`, { headers: h });
      setNewsletterRecipientCount(res.data.count);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to fetch recipients');
      setNewsletterRecipientCount(null);
    }
  };

  const handleNewsletterParseWord = async () => {
    if (!newsletterWordFile) {
      setError('Select a .docx file first');
      return;
    }
    const h = getAuthHeaders();
    if (!h) return;
    const fd = new FormData();
    fd.append('file', newsletterWordFile);
    try {
      const res = await axios.post(`${API_URL}/api/newsletter/parse-word`, fd, {
        headers: { ...h, 'Content-Type': 'multipart/form-data' },
      });
      setNewsletterHtml(res.data.html);
      setSuccess('Word document parsed. Review the content below.');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to parse Word');
    }
  };

  const handleNewsletterPreview = async () => {
    if (!newsletterHtml.trim()) {
      setError('Add content first (parse Word or paste HTML)');
      return;
    }
    const h = getAuthHeaders();
    if (!h) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/newsletter/preview`,
        { html: newsletterHtml, sampleSize: 3 },
        { headers: h }
      );
      setNewsletterPreviewSamples(res.data.samples);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to generate preview');
    }
  };

  const handleNewsletterSend = async () => {
    if (!newsletterHtml.trim() || !newsletterSubject.trim() || !newsletterFrom.trim()) {
      setError('Fill in content, subject, and From address');
      return;
    }
    if (!window.confirm('Send newsletter to all recipients? This action cannot be undone.')) return;
    const h = getAuthHeaders();
    if (!h) return;
    setNewsletterSending(true);
    setError('');
    setSuccess('');
    try {
      const body: Record<string, string> = {
        html: newsletterHtml,
        subject: newsletterSubject,
        from: newsletterFrom,
        listSource: newsletterListSource,
      };
      if (newsletterListSource === 'csv' && newsletterCsvData) body.csvData = newsletterCsvData;
      const res = await axios.post(`${API_URL}/api/newsletter/send`, body, { headers: h });
      setSuccess(res.data.message || `Sent to ${res.data.totalSent} recipients.`);
      setNewsletterPreviewSamples(null);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send newsletter');
    } finally {
      setNewsletterSending(false);
    }
  };

  const handleNewsletterSendBounceReport = async () => {
    const h = getAuthHeaders();
    if (!h) return;
    setNewsletterBounceReportSending(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/newsletter/send-bounce-report`, {}, { headers: h });
      setSuccess(res.data.message || 'Bounce report sent.');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send bounce report');
    } finally {
      setNewsletterBounceReportSending(false);
    }
  };

  const handleNewsletterWordFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setNewsletterWordFile(f);
  };

  const handleNewsletterCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result);
      setNewsletterCsvData(text);
      const lines = text.trim().split(/\r?\n/);
      const header = lines[0]?.toLowerCase().split(',').map((h: string) => h.trim()) || [];
      const emailIdx = header.findIndex((h: string) => h === 'email');
      if (emailIdx >= 0 && lines.length > 1) {
        const emails = new Set<string>();
        for (let i = 1; i < lines.length; i++) {
          const cells = lines[i].split(',');
          const e = (cells[emailIdx] || '').trim().toLowerCase();
          if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) emails.add(e);
        }
        setNewsletterRecipientCount(emails.size);
      } else {
        setNewsletterRecipientCount(null);
      }
    };
    reader.readAsText(f);
  };

  const sendTestEmailRequest = async () => {
    const adminEmail = sessionStorage.getItem('adminEmail');
    const adminPassword = sessionStorage.getItem('adminPassword') || '';
    if (!adminEmail || !adminPassword) {
      setError('Session expired. Please log in again.');
      return;
    }
    setEmailTestSending(true);
    setEmailTestResult(null);
    setError('');
    try {
      const body: { email: string; password: string; to?: string } = {
        email: adminEmail,
        password: adminPassword,
      };
      if (emailTestSendTo.trim()) body.to = emailTestSendTo.trim();
      const response = await axios.post(`${API_URL}/api/admin-auth/test-email`, body);
      setEmailTestResult({
        success: !!response.data.success,
        configOk: !!response.data.configOk,
        sendOk: !!response.data.sendOk,
        backend: response.data.backend,
        error: response.data.error,
        message: response.data.message,
      });
    } catch (err: any) {
      const data = err.response?.data;
      setEmailTestResult({
        success: false,
        configOk: false,
        sendOk: false,
        backend: data?.backend,
        error: data?.error || err.message || 'Request failed',
      });
    } finally {
      setEmailTestSending(false);
    }
  };

  const downloadAbandonedCartsCSV = async (includeCompleted: boolean = false, identifiedOnly: boolean = true) => {
    const adminEmail = sessionStorage.getItem('adminEmail');
    const adminPassword = sessionStorage.getItem('adminPassword');

    if (!adminEmail || !adminPassword) {
      setError('Please log in again to download the report');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (includeCompleted) params.set('includeCompleted', 'true');
      if (identifiedOnly) params.set('anonymous', 'false');
      const query = params.toString();
      const url = `${API_URL}/api/abandoned-carts/export/csv${query ? `?${query}` : ''}`;
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          'x-admin-email': adminEmail,
          'x-admin-password': adminPassword,
        },
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      let filename = `abandoned-carts-${new Date().toISOString().split('T')[0]}.csv`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSuccess('CSV report downloaded successfully!');
    } catch (error: any) {
      const status = error.response?.status;
      let serverError = 'Unknown error';

      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          serverError = parsed.error || serverError;
        } catch {
          serverError = `Blob response (${error.response.data.size} bytes)`;
        }
      } else if (error.response?.data?.error) {
        serverError = error.response.data.error;
      }

      const msg = status ? `Download failed (HTTP ${status}): ${serverError}` : `Download failed: ${error.message || 'Network error'}`;
      console.error('CSV download error:', { url: `${API_URL}/api/abandoned-carts/export/csv`, status, serverError });
      setError(msg);
    }
  };

  const handleCreateAccessory = async () => {
    try {
      // Prepare data for API - exclude technicalSpecs and additionalImages as they're not in schema yet
      const { technicalSpecs, additionalImages, ...apiData } = accessoryForm;
      await axios.post(`${API_URL}/api/accessories`, apiData);
      setSuccess('Accessory created successfully!');
      setIsCreatingAccessory(false);
      setAccessoryForm({
        name: '',
        description: '',
        price: 0,
        priceType: 'one-time',
        priceUnit: '',
        imageFilename: '',
        category: 'accessory',
        isActive: true,
        sortOrder: 0,
        technicalSpecs: '',
        additionalImages: ''
      });
      fetchAccessories();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create accessory');
    }
  };

  const handleUpdateAccessory = async () => {
    if (!editingAccessory) return;
    try {
      // Prepare data for API - exclude technicalSpecs and additionalImages as they're not in schema yet
      const { technicalSpecs, additionalImages, ...apiData } = accessoryForm;
      await axios.patch(`${API_URL}/api/accessories/${editingAccessory.id}`, apiData);
      setSuccess('Accessory updated successfully!');
      setEditingAccessory(null);
      fetchAccessories();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update accessory');
    }
  };

  const handleDeleteAccessory = async (id: string, name: string) => {
    if (!window.confirm(`Delete accessory "${name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/accessories/${id}`);
      setSuccess('Accessory deleted successfully!');
      fetchAccessories();
    } catch (error) {
      setError('Failed to delete accessory');
    }
  };

  const startEditAccessory = (accessory: any) => {
    setEditingAccessory(accessory);
    setAccessoryForm({
      name: accessory.name,
      description: accessory.description || '',
      price: accessory.price,
      priceType: accessory.priceType,
      priceUnit: accessory.priceUnit || '',
      imageFilename: accessory.imageFilename || '',
      category: accessory.category || 'accessory',
      isActive: accessory.isActive !== undefined ? accessory.isActive : true,
      sortOrder: accessory.sortOrder,
      technicalSpecs: accessory.technicalSpecs || '',
      additionalImages: accessory.additionalImages || ''
    });
  };

  // Product CRUD functions
  const handleCreateProduct = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await axios.post(`${API_URL}/api/products`, {
        ...productForm,
        features: JSON.stringify([]) // Empty features array as JSON
      });
      setSuccess('Product created successfully!');
      setIsCreatingProduct(false);
      fetchProducts();
    } catch (error) {
      setError('Failed to create product');
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    try {
      await axios.patch(`${API_URL}/api/products/${editingProduct.id}`, productForm);
      setSuccess('Product updated successfully!');
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      setError('Failed to update product');
    }
  };

  const startEditProduct = (product: any) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      shortName: product.shortName,
      description: product.description || '',
      imageFilename: product.imageFilename || '',
      category: product.category,
      basePrice: product.basePrice,
      sortOrder: product.sortOrder
    });
  };

  // Pricing Plan CRUD functions
  const handleCreatePlan = async () => {
    try {
      await axios.post(`${API_URL}/api/products/pricing-plans`, planForm);
      setSuccess('Pricing plan created successfully!');
      setIsCreatingPlan(false);
      fetchPricingPlans();
    } catch (error) {
      setError('Failed to create pricing plan');
    }
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan) return;
    try {
      await axios.patch(`${API_URL}/api/products/pricing-plans/${editingPlan.id}`, planForm);
      setSuccess('Pricing plan updated successfully!');
      setEditingPlan(null);
      fetchPricingPlans();
    } catch (error) {
      setError('Failed to update pricing plan');
    }
  };

  const startEditPlan = (plan: any) => {
    setEditingPlan(plan);
    setPlanForm({
      productId: plan.productId,
      planType: plan.planType,
      name: plan.name,
      billingCycle: plan.billingCycle,
      monthlyPrice: plan.monthlyPrice,
      annualTotal: plan.annualTotal || 0,
      setupFee: plan.setupFee,
      activationFee: plan.activationFee,
      sortOrder: plan.sortOrder
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const headers = getAuthHeaders();
    if (!headers) {
      setError('Session expired. Please log in again.');
      return;
    }

    const fileArray = Array.from(files);

    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const invalidFiles = fileArray.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setError(`Please select valid image files (JPEG, PNG, WebP, or GIF). Invalid: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Validate file sizes (5MB max per file)
    const oversizedFiles = fileArray.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError(`File size must be less than 5MB. Oversized: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    
    // Use multiple files endpoint if more than one file
    if (fileArray.length > 1) {
      fileArray.forEach(file => {
        formData.append('images', file);
      });
      formData.append('category', selectedCategory);

      try {
        const response = await axios.post(`${API_URL}/api/images/upload-multiple`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...headers,
          },
        });

        const { uploaded, errors } = response.data;
        if (errors && errors.length > 0) {
          setError(`Uploaded ${uploaded} files, but ${errors.length} failed: ${errors.map((e: any) => e.filename).join(', ')}`);
        } else {
          setSuccess(`Successfully uploaded ${uploaded} image${uploaded !== 1 ? 's' : ''}!`);
        }
        fetchImages(); // Refresh the list
        
        // Reset file input
        event.target.value = '';
      } catch (error: any) {
        console.error('Upload error:', error);
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to upload images';
        setError(errorMsg);
      } finally {
        setUploading(false);
      }
    } else {
      // Single file - use original endpoint
      formData.append('image', fileArray[0]);
      formData.append('category', selectedCategory);

      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const response = await axios.post(`${API_URL}/api/images/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...headers,
          },
        });

        setSuccess(`Image "${fileArray[0].name}" uploaded successfully!`);
        fetchImages(); // Refresh the list
        
        // Reset file input
        event.target.value = '';
      } catch (error: any) {
        console.error('Upload error:', error);
        console.error('Error details:', error.response?.data);
        const errorMsg = error.response?.data?.error || error.response?.data?.details?.[0]?.message || 'Failed to upload image';
        setError(errorMsg);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleUpdateImage = async (id: string, updates: Partial<Image>, opts?: { onError?: (msg: string) => void }): Promise<boolean> => {
    const headers = getAuthHeaders();
    if (!headers) {
      setError('Session expired. Please log in again.');
      return false;
    }

    try {
      await axios.put(`${API_URL}/api/images/${id}`, updates, { headers });
      setSuccess('Image updated successfully!');
      fetchImages();
      return true;
    } catch (error: any) {
      console.error('Update error:', error);
      const msg = error.response?.data?.error || 'Failed to update image';
      if (opts?.onError && updates.filename) {
        opts.onError(msg);
      } else {
        setError(msg);
      }
      return false;
    }
  };

  const handleDeleteImage = async (id: string, filename: string) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) {
      setError('Session expired. Please log in again.');
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/images/${id}`, { headers });
      setSuccess('Image deleted successfully!');
      fetchImages();
    } catch (error: any) {
      console.error('Delete error:', error);
      setError(error.response?.data?.error || 'Failed to delete image');
    }
  };

  const toggleImageSelection = (id: string) => {
    const newSelection = new Set(selectedImages);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedImages(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedImages.size === images.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(images.map(img => img.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) {
      setError('No images selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedImages.size} images? This cannot be undone.`)) {
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) {
      setError('Session expired. Please log in again.');
      return;
    }

    setBulkDeleting(true);
    setError('');
    
    try {
      let successCount = 0;
      let failCount = 0;

      for (const id of Array.from(selectedImages)) {
        try {
          await axios.delete(`${API_URL}/api/images/${id}`, { headers });
          successCount++;
        } catch (error) {
          console.error(`Failed to delete image ${id}:`, error);
          failCount++;
        }
      }

      if (failCount > 0) {
        setError(`Deleted ${successCount} images. ${failCount} failed to delete.`);
      } else {
        setSuccess(`Successfully deleted ${successCount} images!`);
      }
      
      fetchImages();
    } catch (error: any) {
      console.error('Bulk delete error:', error);
      setError('Failed to delete images');
    } finally {
      setBulkDeleting(false);
    }
  };

  const selectBlogScreenshots = () => {
    const blogScreenshots = images.filter(img => 
      img.filename.toLowerCase().includes('screenshot') ||
      img.filename.toLowerCase().includes('blog') ||
      img.filename.toLowerCase().startsWith('image 10-')
    );
    setSelectedImages(new Set(blogScreenshots.map(img => img.id)));
    setSuccess(`Selected ${blogScreenshots.length} blog screenshot images`);
  };

  const handleUpdateBrochureStatus = async (id: string, status: string) => {
    const h = getAuthHeaders();
    if (!h) {
      setError('Session expired. Please log in again.');
      return;
    }
    try {
      await axios.patch(`${API_URL}/api/brochure-requests/${id}`, { status }, { headers: h });
      setSuccess('Brochure request status updated successfully!');
      fetchBrochureRequests();
    } catch (error: any) {
      console.error('Update error:', error);
      setError(error.response?.data?.error || 'Failed to update brochure request');
    }
  };

  const handleUpdateBrochureNote = async (id: string, notes: string) => {
    const h = getAuthHeaders();
    if (!h) {
      setError('Session expired. Please log in again.');
      return;
    }
    try {
      await axios.patch(`${API_URL}/api/brochure-requests/${id}`, { notes }, { headers: h });
      setSuccess('Notes updated successfully!');
      setEditingNote(null);
      setNoteText('');
      fetchBrochureRequests();
    } catch (error: any) {
      console.error('Update error:', error);
      setError(error.response?.data?.error || 'Failed to update notes');
    }
  };

  const handleDeleteBrochureRequest = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the request from "${name}"?`)) {
      return;
    }
    const h = getAuthHeaders();
    if (!h) {
      setError('Session expired. Please log in again.');
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/brochure-requests/${id}`, { headers: h });
      setSuccess('Brochure request deleted successfully!');
      fetchBrochureRequests();
    } catch (error: any) {
      console.error('Delete error:', error);
      setError(error.response?.data?.error || 'Failed to delete brochure request');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login-container">
          <div className="admin-login-box">
            <h1>Admin Login</h1>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  autoComplete="username"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showLoginPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showLoginPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
              </div>
              {loginError && <div className="error-message">{loginError}</div>}
              <button type="submit" className="btn btn-primary">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {currentUser && (
            <span style={{ color: '#666', fontSize: '0.9rem' }}>
              {currentUser.fullName || currentUser.email}
            </span>
          )}
          <button 
            onClick={() => setShowProfileModal(true)} 
            className="btn btn-secondary"
            style={{ fontSize: '0.9rem' }}
          >
            Profile
          </button>
          <button onClick={handleLogout} className="btn btn-secondary">
            Logout
          </button>
        </div>
        <p className="page-subtitle">Manage images and brochure requests</p>
      </div>

      {/* Tabs - two rows of equal length: 6+6 when superuser (12 tabs), 4+3 otherwise (7 tabs) */}
      <div className={`admin-tabs ${isSuperUser ? 'admin-tabs--superuser' : 'admin-tabs--standard'}`}>
        <button
          className={`admin-tab ${activeTab === 'images' ? 'active' : ''}`}
          onClick={() => setActiveTab('images')}
        >
          Images
        </button>
        <button
          className={`admin-tab ${activeTab === 'brochures' ? 'active' : ''}`}
          onClick={() => setActiveTab('brochures')}
        >
          Brochures
        </button>
        <button
          className={`admin-tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button
          className={`admin-tab ${activeTab === 'pricing-plans' ? 'active' : ''}`}
          onClick={() => setActiveTab('pricing-plans')}
        >
          Pricing Plans
        </button>
        <button
          className={`admin-tab ${activeTab === 'accessories' ? 'active' : ''}`}
          onClick={() => setActiveTab('accessories')}
        >
          Accessories
        </button>
        <button
          className={`admin-tab ${activeTab === 'abandoned-carts' ? 'active' : ''}`}
          onClick={() => setActiveTab('abandoned-carts')}
        >
          Abandoned Carts
        </button>
        {isSuperUser && (
          <>
            <button
              className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Orders
            </button>
            <button
              className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            <button
              className={`admin-tab ${activeTab === 'access-requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('access-requests')}
            >
              Access Requests
            </button>
            <button
              className={`admin-tab ${activeTab === 'email-test' ? 'active' : ''}`}
              onClick={() => setActiveTab('email-test')}
            >
              Email Test
            </button>
            <button
              className={`admin-tab ${activeTab === 'newsletter' ? 'active' : ''}`}
              onClick={() => setActiveTab('newsletter')}
            >
              Newsletter
            </button>
          </>
        )}
        <Link 
          to="/blog-admin" 
          className="admin-tab"
          style={{ textDecoration: 'none', display: 'inline-block' }}
        >
          Blog Posts
        </Link>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')} className="alert-close">×</button>
        </div>
      )}

      {/* Details View Modal (Orders / Abandoned Carts) - top-level so it renders for any tab */}
      {detailsModalContent && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
          }}
          onClick={() => setDetailsModalContent(null)}
        >
          <div
            style={{
              background: 'white',
              padding: 0,
              borderRadius: '8px',
              maxWidth: '700px',
              width: '95%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{detailsModalContent.title}</h3>
              <button
                type="button"
                onClick={() => setDetailsModalContent(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', padding: '0 0.5rem' }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div style={{ padding: '1rem 1.5rem', overflowY: 'auto', flex: 1 }}>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  userSelect: 'text',
                }}
              >
                {detailsModalContent.json}
              </pre>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #ddd', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(detailsModalContent!.json);
                    setDetailsModalCopied(true);
                    setTimeout(() => setDetailsModalCopied(false), 2000);
                  } catch {
                    // fallback: ignore
                  }
                }}
              >
                {detailsModalCopied ? 'Copied!' : 'Copy to clipboard'}
              </button>
              <button
                type="button"
                className="btn"
                style={{ background: '#6c757d', color: 'white' }}
                onClick={() => setDetailsModalContent(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Images Tab Content */}
      {activeTab === 'images' && (
        <>
          {/* Upload Section */}
          <div className="upload-section">
            <h2>Upload New Image</h2>
            <div className="upload-form">
              <div className="form-group">
                <label htmlFor="category">Category:</label>
                <select
                  id="category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={uploading}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="image-upload">Select Image(s):</label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="file-input"
                  multiple
                />
                <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                  You can select multiple images at once (hold Ctrl/Cmd to select multiple files)
                </small>
              </div>
              
              {uploading && (
                <div className="upload-progress">
                  <div className="spinner"></div>
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          </div>

          {/* Filter Section */}
          <div className="filter-section">
            <h2>Manage Images</h2>
            <div className="filter-controls">
              <label htmlFor="search-images">Search:</label>
              <input
                id="search-images"
                type="text"
                placeholder="Search by filename..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                style={{ 
                  padding: '0.5rem', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  minWidth: '250px'
                }}
              />
              
              <label htmlFor="filter-category">Filter by category:</label>
              <select
                id="filter-category"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              
              <label htmlFor="sort-by">Sort By:</label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="size-desc">Largest First</option>
                <option value="size-asc">Smallest First</option>
              </select>
            </div>
            
            {/* Search Results Count */}
            {searchQuery && (
              <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                Found {images.filter(img => 
                  img.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (img.alt && img.alt.toLowerCase().includes(searchQuery.toLowerCase())) ||
                  (img.description && img.description.toLowerCase().includes(searchQuery.toLowerCase()))
                ).length} image(s) matching "{searchQuery}"
              </div>
            )}
            
            {/* Bulk Actions */}
            <div className="bulk-actions">
              <button 
                onClick={toggleSelectAll}
                className="btn btn-secondary btn-sm"
              >
                {selectedImages.size === images.length ? 'Deselect All' : 'Select All'}
              </button>
              <button 
                onClick={selectBlogScreenshots}
                className="btn btn-secondary btn-sm"
              >
                Select Blog Screenshots
              </button>
              <button 
                onClick={handleBulkDelete}
                disabled={selectedImages.size === 0 || bulkDeleting}
                className="btn btn-danger btn-sm"
              >
                {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedImages.size})`}
              </button>
            </div>
          </div>

          {/* Images Grid */}
          <div className="images-section">
            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <span>Loading images...</span>
              </div>
            ) : images.length === 0 ? (
              <div className="no-images">
                <p>No images found. Upload some images to get started!</p>
              </div>
            ) : (
              <div className="images-grid">
                {images
                  .filter(img => filterCategory === 'all' || img.category === filterCategory)
                  .filter(img => 
                    searchQuery === '' || 
                    img.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (img.alt && img.alt.toLowerCase().includes(searchQuery.toLowerCase())) ||
                    (img.description && img.description.toLowerCase().includes(searchQuery.toLowerCase()))
                  )
                  .sort((a, b) => {
                    switch (sortBy) {
                      case 'date-desc':
                        return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
                      case 'date-asc':
                        return new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime();
                      case 'name-asc':
                        return a.filename.localeCompare(b.filename);
                      case 'name-desc':
                        return b.filename.localeCompare(a.filename);
                      case 'size-desc':
                        return b.size - a.size;
                      case 'size-asc':
                        return a.size - b.size;
                      default:
                        return 0;
                    }
                  })
                  .map((image) => (
                  <div key={image.id} className={`image-card ${selectedImages.has(image.id) ? 'selected' : ''}`}>
                    <div className="image-select-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedImages.has(image.id)}
                        onChange={() => toggleImageSelection(image.id)}
                      />
                    </div>
                    <div className="image-preview">
                      <img
                        src={`${API_URL}${image.thumbnailUrl}`}
                        alt={image.alt || image.filename}
                        loading="lazy"
                      />
                      <div className="image-overlay">
                        <button
                          onClick={() => window.open(`${API_URL}${image.url}`, '_blank')}
                          className="btn btn-sm btn-secondary"
                        >
                          View Full
                        </button>
                        {(userRole === 'manager' || userRole === 'admin' || userRole === 'super_admin' || isSuperUser) && (
                          <button
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = `${API_URL}${image.url}`;
                              link.download = image.filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="btn btn-sm btn-primary"
                            style={{ marginLeft: '0.5rem' }}
                          >
                            Download
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="image-details">
                      <div className="form-group">
                        <label>Filename:</label>
                        <input
                          type="text"
                          className={`image-filename-input ${filenameError[image.id] ? 'input-error' : ''}`}
                          value={editingFilename[image.id] ?? image.filename}
                          onChange={(e) => {
                            setEditingFilename(prev => ({ ...prev, [image.id]: e.target.value }));
                            if (filenameError[image.id]) {
                              setFilenameError(prev => { const next = { ...prev }; delete next[image.id]; return next; });
                            }
                          }}
                          onBlur={async () => {
                            const value = (editingFilename[image.id] ?? image.filename).trim();
                            if (!value) {
                              setFilenameError(prev => ({ ...prev, [image.id]: 'Filename cannot be empty' }));
                              setEditingFilename(prev => { const next = { ...prev }; delete next[image.id]; return next; });
                              return;
                            }
                            if (value.includes('/') || value.includes('\\')) {
                              setFilenameError(prev => ({ ...prev, [image.id]: 'Filename cannot contain / or \\' }));
                              return;
                            }
                            if (!/\.(jpg|jpeg|png|webp|gif)$/i.test(value)) {
                              setFilenameError(prev => ({ ...prev, [image.id]: 'Must end with .jpg, .jpeg, .png, .webp, or .gif' }));
                              return;
                            }
                            if (value === image.filename) {
                              setEditingFilename(prev => { const next = { ...prev }; delete next[image.id]; return next; });
                              setFilenameError(prev => { const next = { ...prev }; delete next[image.id]; return next; });
                              return;
                            }
                            setFilenameError(prev => { const next = { ...prev }; delete next[image.id]; return next; });
                            const ok = await handleUpdateImage(image.id, { filename: value }, {
                              onError: (msg) => setFilenameError(prev => ({ ...prev, [image.id]: msg })),
                            });
                            if (ok) {
                              setEditingFilename(prev => { const next = { ...prev }; delete next[image.id]; return next; });
                            }
                          }}
                          placeholder="e.g. product-image.jpg"
                        />
                        {filenameError[image.id] && (
                          <span className="filename-error">{filenameError[image.id]}</span>
                        )}
                        <small className="filename-hint">Renaming may require updating product/accessory image references.</small>
                      </div>
                      <div className="image-meta">
                        <span className="category-badge">{image.category}</span>
                        <span className="size-info">
                          {image.width}×{image.height} • {formatFileSize(image.size)}
                        </span>
                      </div>
                      
                      <div className="image-controls">
                        <div className="form-group">
                          <label>Category:</label>
                          <select
                            value={image.category}
                            onChange={(e) => handleUpdateImage(image.id, { category: e.target.value })}
                          >
                            {categories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="form-group">
                          <label>Alt Text:</label>
                          <input
                            type="text"
                            value={image.alt || ''}
                            onChange={(e) => handleUpdateImage(image.id, { alt: e.target.value })}
                            placeholder="Alt text for accessibility"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={image.isActive}
                              onChange={(e) => handleUpdateImage(image.id, { isActive: e.target.checked })}
                            />
                            Active
                          </label>
                        </div>
                        
                        <button
                          onClick={() => handleDeleteImage(image.id, image.filename)}
                          className="btn btn-danger btn-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Brochure Requests Tab Content */}
      {activeTab === 'brochures' && (
        <>
          {/* Filter Section */}
          <div className="filter-section">
            <h2>Brochure Requests</h2>
            <div className="filter-controls">
              <label htmlFor="filter-status">Filter by status:</label>
              <select
                id="filter-status"
                value={brochureStatusFilter}
                onChange={(e) => setBrochureStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="fulfilled">Fulfilled</option>
              </select>
            </div>
          </div>

          {/* Brochure Requests Table */}
          <div className="brochure-requests-section">
            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <span>Loading brochure requests...</span>
              </div>
            ) : brochureRequests.length === 0 ? (
              <div className="no-requests">
                <p>No brochure requests found.</p>
              </div>
            ) : (
              <div className="brochure-table-container">
                <table className="brochure-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brochureRequests.map((request) => (
                      <tr key={request.id}>
                        <td className="date-cell">{formatDate(request.requestedAt)}</td>
                        <td className="name-cell">{request.name}</td>
                        <td className="email-cell">
                          <a href={`mailto:${request.email}`}>{request.email}</a>
                        </td>
                        <td className="phone-cell">
                          <a href={`tel:${request.phone}`}>{request.phone}</a>
                        </td>
                        <td className="address-cell">
                          {request.address}, {request.city}, {request.state} {request.zipCode}
                        </td>
                        <td className="status-cell">
                          <select
                            value={request.status}
                            onChange={(e) => handleUpdateBrochureStatus(request.id, e.target.value)}
                            className={`status-select status-${request.status}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="sent">Sent</option>
                            <option value="fulfilled">Fulfilled</option>
                          </select>
                        </td>
                        <td className="notes-cell">
                          {editingNote === request.id ? (
                            <div className="note-edit">
                              <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Add notes..."
                                rows={2}
                              />
                              <div className="note-actions">
                                <button
                                  onClick={() => handleUpdateBrochureNote(request.id, noteText)}
                                  className="btn btn-sm btn-primary"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingNote(null);
                                    setNoteText('');
                                  }}
                                  className="btn btn-sm btn-secondary"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="note-display">
                              <span>{request.notes || 'No notes'}</span>
                              <button
                                onClick={() => {
                                  setEditingNote(request.id);
                                  setNoteText(request.notes || '');
                                }}
                                className="btn-edit-note"
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleDeleteBrochureRequest(request.id, request.name)}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Accessories Tab Content */}
      {activeTab === 'accessories' && (
        <>
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Manage Accessories</h2>
              <button 
                onClick={() => {
                  setIsCreatingAccessory(true);
                  setAccessoryForm({
                    name: '',
                    description: '',
                    price: 0,
                    priceType: 'one-time',
                    priceUnit: '',
                    imageFilename: '',
                    category: 'accessory',
                    isActive: true,
                    sortOrder: accessories.length + 1,
                    technicalSpecs: '',
                    additionalImages: ''
                  });
                }}
                className="btn btn-primary"
              >
                + Create New Accessory
              </button>
            </div>
            
            {/* Create/Edit Form */}
            {(isCreatingAccessory || editingAccessory) && (
              <div className="form-card" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h3>{editingAccessory ? 'Edit Accessory' : 'Create New Accessory'}</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={accessoryForm.name}
                      onChange={(e) => setAccessoryForm({ ...accessoryForm, name: e.target.value })}
                      placeholder="e.g., Emergency Lock Box"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description (optional):</label>
                    <textarea
                      value={accessoryForm.description}
                      onChange={(e) => setAccessoryForm({ ...accessoryForm, description: e.target.value })}
                      placeholder="Brief description"
                      rows={2}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Price:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={accessoryForm.price}
                        onChange={(e) => setAccessoryForm({ ...accessoryForm, price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Price Type:</label>
                      <select
                        value={accessoryForm.priceType}
                        onChange={(e) => setAccessoryForm({ ...accessoryForm, priceType: e.target.value })}
                      >
                        <option value="one-time">One-Time</option>
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Price Unit:</label>
                      <input
                        type="text"
                        value={accessoryForm.priceUnit}
                        onChange={(e) => setAccessoryForm({ ...accessoryForm, priceUnit: e.target.value })}
                        placeholder="/mo, /month, or empty"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Category:</label>
                      <select
                        value={accessoryForm.category}
                        onChange={(e) => setAccessoryForm({ ...accessoryForm, category: e.target.value })}
                      >
                        <option value="accessory">Accessory</option>
                        <option value="protection">Protection</option>
                        <option value="monitoring">Monitoring</option>
                        <option value="safety">Safety</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={accessoryForm.isActive}
                          onChange={(e) => setAccessoryForm({ ...accessoryForm, isActive: e.target.checked })}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Active (visible on site)
                      </label>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Main Image Filename:</label>
                      <input
                        type="text"
                        value={accessoryForm.imageFilename}
                        onChange={(e) => setAccessoryForm({ ...accessoryForm, imageFilename: e.target.value })}
                        placeholder="e.g., accessories-Lock-Box.jpg"
                      />
                      <small style={{ color: '#666', fontSize: '0.85rem' }}>
                        Upload image first via Images tab, then enter exact filename here
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Sort Order:</label>
                      <input
                        type="number"
                        value={accessoryForm.sortOrder}
                        onChange={(e) => setAccessoryForm({ ...accessoryForm, sortOrder: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Additional Images (comma-separated filenames):</label>
                    <input
                      type="text"
                      value={accessoryForm.additionalImages}
                      onChange={(e) => setAccessoryForm({ ...accessoryForm, additionalImages: e.target.value })}
                      placeholder="e.g., image1.jpg, image2.jpg, image3.jpg"
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                      Optional: Additional image filenames separated by commas (for future use)
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Technical Specifications:</label>
                    <textarea
                      value={accessoryForm.technicalSpecs}
                      onChange={(e) => setAccessoryForm({ ...accessoryForm, technicalSpecs: e.target.value })}
                      placeholder="Enter technical specifications, dimensions, features, etc. (one per line or formatted as needed)"
                      rows={6}
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                      Optional: Technical details, dimensions, materials, compatibility info, etc. (for future use)
                    </small>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={editingAccessory ? handleUpdateAccessory : handleCreateAccessory}
                      className="btn btn-primary"
                    >
                      {editingAccessory ? 'Update Accessory' : 'Create Accessory'}
                    </button>
                    <button
                      onClick={() => {
                        setIsCreatingAccessory(false);
                        setEditingAccessory(null);
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <p>Loading accessories...</p>
            ) : (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Sort</th>
                      <th>Name</th>
                      <th>Price</th>
                      <th>Type</th>
                      <th>Image</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessories.map((acc) => (
                      <tr key={acc.id}>
                        <td>{acc.sortOrder}</td>
                        <td>{acc.name}</td>
                        <td>${acc.price.toFixed(2)}{acc.priceUnit}</td>
                        <td>{acc.priceType}</td>
                        <td>{acc.imageFilename || 'No image'}</td>
                        <td>{acc.isActive ? '✓ Active' : '✗ Inactive'}</td>
                        <td className="actions-cell">
                          <button
                            onClick={() => startEditAccessory(acc)}
                            className="btn btn-secondary btn-sm"
                            style={{ marginRight: '0.5rem' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAccessory(acc.id, acc.name)}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {accessories.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center' }}>
                          No accessories found. Click "Create New Accessory" to add one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Products Tab Content */}
      {activeTab === 'products' && (
        <>
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Manage Products</h2>
              <button 
                onClick={() => {
                  setIsCreatingProduct(true);
                  setProductForm({
                    name: '',
                    shortName: '',
                    description: '',
                    imageFilename: '',
                    category: 'in-home',
                    basePrice: 0,
                    sortOrder: products.length + 1
                  });
                }}
                className="btn btn-primary"
              >
                + Create New Product
              </button>
            </div>
            
            {/* Create/Edit Form */}
            {(isCreatingProduct || editingProduct) && (
              <div className="form-card" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h3>{editingProduct ? 'Edit Product' : 'Create New Product'}</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Product Name:</label>
                      <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        placeholder="e.g., EZ Care In-Home Medical Alert"
                      />
                    </div>
                    <div className="form-group">
                      <label>Short Name (URL):</label>
                      <input
                        type="text"
                        value={productForm.shortName}
                        onChange={(e) => setProductForm({ ...productForm, shortName: e.target.value })}
                        placeholder="e.g., ez-care"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description:</label>
                    <textarea
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      placeholder="Product description"
                      rows={3}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Category:</label>
                      <select
                        value={productForm.category}
                        onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                      >
                        <option value="in-home">In-Home</option>
                        <option value="on-the-go">On-the-Go</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Base Price:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={productForm.basePrice}
                        onChange={(e) => setProductForm({ ...productForm, basePrice: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Image Filename:</label>
                      <input
                        type="text"
                        value={productForm.imageFilename}
                        onChange={(e) => setProductForm({ ...productForm, imageFilename: e.target.value })}
                        placeholder="product-image.jpg"
                      />
                    </div>
                    <div className="form-group">
                      <label>Sort Order:</label>
                      <input
                        type="number"
                        value={productForm.sortOrder}
                        onChange={(e) => setProductForm({ ...productForm, sortOrder: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                      className="btn btn-primary"
                    >
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </button>
                    <button
                      onClick={() => {
                        setIsCreatingProduct(false);
                        setEditingProduct(null);
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <p>Loading products...</p>
            ) : (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Sort</th>
                      <th>Name</th>
                      <th>Short Name</th>
                      <th>Category</th>
                      <th>Base Price</th>
                      <th>Image</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((prod) => (
                      <tr key={prod.id}>
                        <td>{prod.sortOrder}</td>
                        <td>{prod.name}</td>
                        <td>{prod.shortName}</td>
                        <td>{prod.category}</td>
                        <td>${prod.basePrice?.toFixed(2)}</td>
                        <td>{prod.imageFilename || 'No image'}</td>
                        <td>{prod.isActive ? '✓ Active' : '✗ Inactive'}</td>
                        <td className="actions-cell">
                          <button
                            onClick={() => startEditProduct(prod)}
                            className="btn btn-secondary btn-sm"
                            style={{ marginRight: '0.5rem' }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Pricing Plans Tab Content */}
      {activeTab === 'pricing-plans' && (
        <>
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Manage Pricing Plans</h2>
              <button 
                onClick={() => {
                  setIsCreatingPlan(true);
                  setPlanForm({
                    productId: products[0]?.id || '',
                    planType: 'annual',
                    name: '',
                    billingCycle: 'annual',
                    monthlyPrice: 0,
                    annualTotal: 0,
                    setupFee: 0,
                    activationFee: 0,
                    sortOrder: pricingPlans.length + 1
                  });
                }}
                className="btn btn-primary"
                disabled={products.length === 0}
              >
                + Create New Pricing Plan
              </button>
            </div>
            
            {/* Create/Edit Form */}
            {(isCreatingPlan || editingPlan) && (
              <div className="form-card" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h3>{editingPlan ? 'Edit Pricing Plan' : 'Create New Pricing Plan'}</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Product:</label>
                      <select
                        value={planForm.productId}
                        onChange={(e) => setPlanForm({ ...planForm, productId: e.target.value })}
                      >
                        <option value="">Select a product...</option>
                        {products.map(prod => (
                          <option key={prod.id} value={prod.id}>{prod.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Plan Name:</label>
                      <input
                        type="text"
                        value={planForm.name}
                        onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                        placeholder="e.g., Pay Annually - Save 33%"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Billing Cycle:</label>
                      <select
                        value={planForm.billingCycle}
                        onChange={(e) => setPlanForm({ ...planForm, billingCycle: e.target.value })}
                      >
                        <option value="annual">Annual</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Plan Type:</label>
                      <select
                        value={planForm.planType}
                        onChange={(e) => setPlanForm({ ...planForm, planType: e.target.value })}
                      >
                        <option value="annual">Annual</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Monthly Price:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.monthlyPrice}
                        onChange={(e) => setPlanForm({ ...planForm, monthlyPrice: parseFloat(e.target.value) || 0 })}
                        placeholder="19.99"
                      />
                    </div>
                    <div className="form-group">
                      <label>Annual Total:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.annualTotal}
                        onChange={(e) => setPlanForm({ ...planForm, annualTotal: parseFloat(e.target.value) || 0 })}
                        placeholder="239.88"
                      />
                      <small style={{ fontSize: '0.8rem', color: '#666' }}>0 for monthly plans</small>
                    </div>
                    <div className="form-group">
                      <label>Setup Fee:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.setupFee}
                        onChange={(e) => setPlanForm({ ...planForm, setupFee: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Activation Fee:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.activationFee}
                        onChange={(e) => setPlanForm({ ...planForm, activationFee: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Sort Order:</label>
                    <input
                      type="number"
                      value={planForm.sortOrder}
                      onChange={(e) => setPlanForm({ ...planForm, sortOrder: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={editingPlan ? handleUpdatePlan : handleCreatePlan}
                      className="btn btn-primary"
                    >
                      {editingPlan ? 'Update Pricing Plan' : 'Create Pricing Plan'}
                    </button>
                    <button
                      onClick={() => {
                        setIsCreatingPlan(false);
                        setEditingPlan(null);
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <p>Loading pricing plans...</p>
            ) : (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Sort</th>
                      <th>Product</th>
                      <th>Plan Name</th>
                      <th>Billing Cycle</th>
                      <th>Monthly Price</th>
                      <th>Annual Total</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingPlans.map((plan) => (
                      <tr key={plan.id}>
                        <td>{plan.sortOrder}</td>
                        <td>{plan.productName}</td>
                        <td>{plan.name}</td>
                        <td>{plan.billingCycle}</td>
                        <td>${plan.monthlyPrice?.toFixed(2)}</td>
                        <td>{plan.annualTotal ? `$${plan.annualTotal.toFixed(2)}` : 'N/A'}</td>
                        <td>{plan.isActive ? '✓ Active' : '✗ Inactive'}</td>
                        <td className="actions-cell">
                          <button
                            onClick={() => startEditPlan(plan)}
                            className="btn btn-secondary btn-sm"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Abandoned Carts Tab Content */}
      {activeTab === 'abandoned-carts' && (
        <>
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Abandoned Shopping Carts</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={generateAbandonedCartsReport}
                  className="btn btn-primary"
                  disabled={abandonedCartsReportSending || loading}
                  title="Send yesterday's abandoned carts daily report to configured email"
                >
                  {abandonedCartsReportSending ? 'Sending…' : '📧 Send Daily Report'}
                </button>
                <button 
                  onClick={() => downloadAbandonedCartsCSV(false)}
                  className="btn btn-secondary"
                >
                  📥 Download CSV (Incomplete Only)
                </button>
                <button 
                  onClick={() => downloadAbandonedCartsCSV(true)}
                  className="btn btn-secondary"
                >
                  📥 Download CSV (All)
                </button>
              </div>
            </div>
            
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              View and download abandoned shopping cart data. Carts are automatically saved when customers start the checkout process.
            </p>

            {loading ? (
              <p>Loading abandoned carts...</p>
            ) : (
              <>
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '4px' }}>
                  <strong>Total:</strong> {abandonedCartStats?.total ?? abandonedCarts.length}
                  {' | '}
                  <strong>Anonymous:</strong> {abandonedCartStats?.anonymousCount ?? 0} (count only)
                  {' | '}
                  <strong>Identified:</strong> {abandonedCartStats?.identifiedCount ?? 0}
                  {' | '}
                  <strong>Not Contacted:</strong> {abandonedCarts.filter((c: any) => !c.contacted).length}
                  {' | '}
                  <strong>Contacted:</strong> {abandonedCarts.filter((c: any) => c.contacted).length}
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500 }}>Filter:</span>
                  <button
                    className={`btn btn-sm ${abandonedCartFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setAbandonedCartFilter('all'); fetchAbandonedCarts('all'); }}
                  >
                    All
                  </button>
                  <button
                    className={`btn btn-sm ${abandonedCartFilter === 'identified' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setAbandonedCartFilter('identified'); fetchAbandonedCarts('identified'); }}
                  >
                    Identified only
                  </button>
                  <button
                    className={`btn btn-sm ${abandonedCartFilter === 'anonymous' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setAbandonedCartFilter('anonymous'); fetchAbandonedCarts('anonymous'); }}
                  >
                    Anonymous only
                  </button>
                </div>
                
                {abandonedCarts.length === 0 ? (
                  <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    No abandoned carts found.
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          {abandonedCartFilter === 'all' && <th>Type</th>}
                          <th>Abandoned At</th>
                          {abandonedCartFilter !== 'anonymous' && (
                            <>
                              <th>Customer Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                            </>
                          )}
                          <th>Product</th>
                          <th>Plan</th>
                          {abandonedCartFilter !== 'anonymous' && <th>Contacted</th>}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {abandonedCarts.map((cart: any) => (
                          <tr key={cart.id}>
                            {abandonedCartFilter === 'all' && (
                              <td>{cart.isAnonymous ? 'Anonymous' : 'Identified'}</td>
                            )}
                            <td>{new Date(cart.abandonedAt).toLocaleString()}</td>
                            {abandonedCartFilter !== 'anonymous' && (
                              <>
                                <td>{cart.customerFullName || 'N/A'}</td>
                                <td>{cart.customerEmail || 'N/A'}</td>
                                <td>{cart.customerPhoneNumber || 'N/A'}</td>
                              </>
                            )}
                            <td>{cart.productName || 'N/A'}</td>
                            <td>{cart.planName || 'N/A'}</td>
                            {abandonedCartFilter !== 'anonymous' && (
                              <td>{cart.contacted ? '✓ Yes' : '✗ No'}</td>
                            )}
                            <td className="actions-cell">
                              <button
                                onClick={() => {
                                  const cartDetails = {
                                    ...cart,
                                    cartItems: typeof cart.cartItems === 'string' ? JSON.parse(cart.cartItems) : cart.cartItems,
                                    completedSections: typeof cart.completedSections === 'string' ? JSON.parse(cart.completedSections) : cart.completedSections,
                                    pricingData: cart.pricingData ? (typeof cart.pricingData === 'string' ? JSON.parse(cart.pricingData) : cart.pricingData) : null,
                                  };
                                  setDetailsModalContent({
                                    title: `Cart ${cart.id || cart.customerEmail || 'Details'}`,
                                    json: JSON.stringify(cartDetails, null, 2),
                                  });
                                }}
                                className="btn btn-secondary btn-sm"
                                style={{ marginRight: '0.5rem' }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Orders Tab Content (Super-User Only) */}
      {activeTab === 'orders' && isSuperUser && (
        <>
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Saved Orders</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={generateOrdersReport}
                  className="btn btn-primary"
                  disabled={ordersReportSending || loading}
                  title="Send yesterday's orders as encrypted CSV to ORDER_REPORT_EMAIL"
                >
                  {ordersReportSending ? 'Sending…' : '📧 Generate Daily Report'}
                </button>
                <button onClick={fetchOrders} className="btn btn-secondary" disabled={loading}>
                  {loading ? 'Loading…' : '↻ Refresh'}
                </button>
              </div>
            </div>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Orders placed through checkout. Includes orders saved locally when the LifeBeacon API fails (e.g. 403/500). External Order ID indicates successful send to LifeBeacon.
            </p>
            {loading ? (
              <p>Loading orders…</p>
            ) : (
              <>
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '4px' }}>
                  <strong>Total:</strong> {orders.length}
                  {' | '}
                  <strong>With External ID:</strong> {orders.filter((o: any) => o.externalOrderId).length}
                  {' | '}
                  <strong>Saved locally only:</strong> {orders.filter((o: any) => !o.externalOrderId).length}
                </div>
                {orders.length === 0 ? (
                  <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No orders found.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Confirmation</th>
                          <th>Customer</th>
                          <th>Email</th>
                          <th>Product / Plan</th>
                          <th>Total Today</th>
                          <th>External ID</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order: any) => (
                          <tr key={order.id}>
                            <td><code style={{ fontSize: '0.85rem' }}>{order.orderId}</code></td>
                            <td>{order.confirmationNumber || '—'}</td>
                            <td>{order.customerFullName || '—'}</td>
                            <td>{order.customerEmail || '—'}</td>
                            <td>{[order.productName, order.planName].filter(Boolean).join(' / ') || '—'}</td>
                            <td>${Number(order.totalToday || 0).toFixed(2)}</td>
                            <td>{order.externalOrderId ? <code>{order.externalOrderId}</code> : <span style={{ color: '#999' }}>—</span>}</td>
                            <td>{mapOrderStatusForDisplay(order.orderStatus) || '—'}</td>
                            <td>{order.createdAt ? new Date(order.createdAt).toLocaleString() : '—'}</td>
                            <td className="actions-cell">
                              <button
                                onClick={() => {
                                  setDetailsModalContent({
                                    title: `Order ${order.orderId}`,
                                    json: JSON.stringify(order, null, 2),
                                  });
                                }}
                                className="btn btn-secondary btn-sm"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Users Tab Content (Super-User Only) */}
      {activeTab === 'users' && isSuperUser && (
        <>
          <div className="admin-section">
            <h2>Admin Users</h2>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Manage admin users. Only super-users can create and manage other admin users.
            </p>

            <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px' }}>
              <h3>Create New Admin User</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    placeholder="user@example.com"
                    style={{ width: '100%', padding: '0.5rem' }}
                  />
                </div>
                <div>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={newUserForm.fullName}
                    onChange={(e) => setNewUserForm({ ...newUserForm, fullName: e.target.value })}
                    placeholder="Full Name"
                    style={{ width: '100%', padding: '0.5rem' }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Password * (min 8 characters)</label>
                <input
                  type="text"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  placeholder="Password"
                  style={{ width: '100%', padding: '0.5rem' }}
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                  Password will be visible while creating. The new user will receive an email notification.
                </small>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label>Role *</label>
                <select
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem' }}
                >
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="viewer">Viewer</option>
                </select>
                <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                  Admin: Full content management access. Manager: Can edit but not delete. Viewer: Read-only access.
                </small>
              </div>
              <button onClick={() => handleCreateUser()} className="btn btn-primary">
                Create User
              </button>
            </div>

            {loading ? (
              <p>Loading users...</p>
            ) : (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Full Name</th>
                      <th>Role</th>
                      <th>Super User</th>
                      <th>Active</th>
                      <th>Last Login</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.email}</td>
                        <td>{user.fullName || '-'}</td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            background: user.role === 'super_admin' ? '#721c24' :
                                        user.role === 'admin' ? '#004085' :
                                        user.role === 'manager' ? '#856404' : '#6c757d',
                            color: 'white',
                            fontSize: '0.875rem',
                            textTransform: 'capitalize'
                          }}>
                            {user.role || 'admin'}
                          </span>
                        </td>
                        <td>{user.isSuperUser ? '✓ Yes' : '✗ No'}</td>
                        <td>{user.isActive ? '✓ Active' : '✗ Inactive'}</td>
                        <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}</td>
                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="btn btn-sm"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleUserActive(user)}
                              className={`btn btn-sm ${user.isActive ? 'btn-warning' : 'btn-success'}`}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                              disabled={user.isSuperUser && !user.isActive}
                            >
                              {user.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}>
                <div style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '8px',
                  maxWidth: '500px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                }}>
                  <h3>Edit User: {editingUser.email}</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={editUserForm.fullName}
                      onChange={(e) => setEditUserForm({ ...editUserForm, fullName: e.target.value })}
                      placeholder="Full Name"
                      style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Role *</label>
                    <select
                      value={editUserForm.role}
                      onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                      disabled={editingUser.isSuperUser}
                    >
                      <option value="super_admin">Super Admin</option>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    {editingUser.isSuperUser && (
                      <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                        Super-user role cannot be changed
                      </small>
                    )}
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={editUserForm.isActive}
                        onChange={(e) => setEditUserForm({ ...editUserForm, isActive: e.target.checked })}
                        disabled={editingUser.isSuperUser && !editUserForm.isActive}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Active
                    </label>
                    {editingUser.isSuperUser && (
                      <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                        Super-user cannot be deactivated
                      </small>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setEditingUser(null)}
                      className="btn"
                      style={{ background: '#6c757d' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleUpdateUser()}
                      className="btn btn-primary"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Password Confirmation Modal */}
            {showPasswordModal && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1001,
              }}>
                <div style={{
                  background: 'white',
                  padding: '2rem',
                  borderRadius: '8px',
                  maxWidth: '400px',
                  width: '90%',
                }}>
                  <h3>Confirm Password</h3>
                  <p style={{ marginBottom: '1rem', color: '#666' }}>
                    Please enter your password to continue:
                  </p>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswordConfirmModal ? 'text' : 'password'}
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        placeholder="Enter your password"
                        style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', paddingRight: '2.5rem' }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handlePasswordModalSubmit();
                          }
                        }}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPasswordConfirmModal(!showPasswordConfirmModal)}
                        aria-label={showPasswordConfirmModal ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                      >
                        {showPasswordConfirmModal ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setShowPasswordModal(false);
                        setPasswordConfirm('');
                        setPasswordModalCallback(null);
                        setShowPasswordConfirmModal(false);
                      }}
                      className="btn"
                      style={{ background: '#6c757d' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePasswordModalSubmit}
                      className="btn btn-primary"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Access Requests Tab Content (Super-User Only) */}
      {activeTab === 'access-requests' && isSuperUser && (
        <>
          <div className="admin-section">
            <h2>Access Requests</h2>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Review and approve/reject admin access requests from users.
            </p>

            {loading ? (
              <p>Loading access requests...</p>
            ) : (
              <>
                {accessRequests.length === 0 ? (
                  <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    No pending access requests.
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Full Name</th>
                          <th>Status</th>
                          <th>Requested At</th>
                          <th>Reviewed By</th>
                          <th>Notes</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessRequests.map((request) => (
                          <tr key={request.id}>
                            <td>{request.email}</td>
                            <td>{request.fullName || '-'}</td>
                            <td>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                background: request.status === 'approved' ? '#d4edda' : 
                                            request.status === 'rejected' ? '#f8d7da' : '#fff3cd',
                                color: request.status === 'approved' ? '#155724' : 
                                       request.status === 'rejected' ? '#721c24' : '#856404'
                              }}>
                                {request.status}
                              </span>
                            </td>
                            <td>{new Date(request.requestedAt).toLocaleString()}</td>
                            <td>{request.reviewedBy || '-'}</td>
                            <td>{request.notes || '-'}</td>
                            <td>
                              {request.status === 'pending' && (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={() => setReviewingRequest(request)}
                                    className="btn btn-primary btn-sm"
                                  >
                                    Review
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {reviewingRequest && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '2rem',
                      borderRadius: '8px',
                      maxWidth: '500px',
                      width: '90%'
                    }}>
                      <h3>Review Access Request</h3>
                      <p><strong>Email:</strong> {reviewingRequest.email}</p>
                      <p><strong>Name:</strong> {reviewingRequest.fullName || 'Not provided'}</p>
                      <p><strong>Requested:</strong> {new Date(reviewingRequest.requestedAt).toLocaleString()}</p>
                      
                      <div style={{ marginTop: '1rem' }}>
                        <label>Set Password (for approval) *</label>
                        <div className="password-input-wrapper">
                          <input
                            type={showReviewPassword ? 'text' : 'password'}
                            value={reviewPassword}
                            onChange={(e) => setReviewPassword(e.target.value)}
                            placeholder="Min 8 characters"
                            style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', paddingRight: '2.5rem' }}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowReviewPassword(!showReviewPassword)}
                            aria-label={showReviewPassword ? 'Hide password' : 'Show password'}
                            tabIndex={-1}
                          >
                            {showReviewPassword ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <label>Notes</label>
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          placeholder="Optional notes"
                          style={{ width: '100%', padding: '0.5rem', minHeight: '80px', marginBottom: '1rem' }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleReviewRequest(reviewingRequest.id, 'approve')}
                          className="btn btn-primary"
                          disabled={!reviewPassword || reviewPassword.length < 8}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReviewRequest(reviewingRequest.id, 'reject')}
                          className="btn btn-secondary"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => {
                            setReviewingRequest(null);
                            setReviewPassword('');
                            setReviewNotes('');
                            setShowReviewPassword(false);
                          }}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Newsletter Tab Content (Super-User Only) */}
      {activeTab === 'newsletter' && isSuperUser && (
        <div className="admin-section">
          <h2>Newsletter</h2>
          {newsletterConfig && !newsletterConfig.configured && (
            <div style={{ padding: '1rem', background: '#f8d7da', color: '#721c24', borderRadius: '8px', marginBottom: '1rem' }}>
              Resend is not configured. Set <code>RESEND_API_KEY</code> in the server environment.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '800px' }}>
            <div>
              <h3 style={{ marginBottom: '0.5rem' }}>1. List Source</h3>
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label>
                  <input
                    type="radio"
                    checked={newsletterListSource === 'system'}
                    onChange={() => { setNewsletterListSource('system'); setNewsletterRecipientCount(null); }}
                  />
                  {' '}Pull from system (BrochureRequest, Orders, AbandonedCart, Customer)
                </label>
                <label>
                  <input
                    type="radio"
                    checked={newsletterListSource === 'csv'}
                    onChange={() => { setNewsletterListSource('csv'); setNewsletterRecipientCount(null); }}
                  />
                  {' '}Upload CSV
                </label>
                {newsletterListSource === 'system' && (
                  <button type="button" className="btn btn-secondary" onClick={fetchNewsletterRecipients}>
                    Get Count
                  </button>
                )}
                {newsletterListSource === 'csv' && (
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleNewsletterCsvFileChange}
                  />
                )}
                {newsletterRecipientCount !== null && (
                  <span><strong>{newsletterRecipientCount}</strong> recipients</span>
                )}
              </div>
              {newsletterListSource === 'csv' && newsletterCsvData && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                  CSV loaded. Columns: email (required), firstName, lastName (optional).
                </p>
              )}
            </div>
            <div>
              <h3 style={{ marginBottom: '0.5rem' }}>2. Content</h3>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                Use placeholders: <code>{'{{firstName}}'}</code>, <code>{'{{lastName}}'}</code>, <code>{'{{email}}'}</code>. Fallback: <code>{'{{firstName|there}}'}</code>.
              </p>
              <div style={{ marginBottom: '0.5rem' }}>
                <input type="file" accept=".docx" onChange={handleNewsletterWordFileChange} />
                <button type="button" className="btn btn-secondary" style={{ marginLeft: '0.5rem' }} onClick={handleNewsletterParseWord} disabled={!newsletterWordFile}>
                  Parse Word
                </button>
              </div>
              <textarea
                value={newsletterHtml}
                onChange={(e) => setNewsletterHtml(e.target.value)}
                placeholder="Paste HTML or parse from Word..."
                rows={12}
                style={{ width: '100%', padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.9rem' }}
              />
            </div>
            <div>
              <h3 style={{ marginBottom: '0.5rem' }}>3. Preview</h3>
              <button type="button" className="btn btn-secondary" onClick={handleNewsletterPreview} disabled={!newsletterHtml.trim()}>
                Preview (3 samples)
              </button>
              {newsletterPreviewSamples && newsletterPreviewSamples.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  {newsletterPreviewSamples.map((s, i) => (
                    <div key={i} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', background: '#f9f9f9' }}>
                      <strong>To: {s.email}</strong>
                      <div dangerouslySetInnerHTML={{ __html: s.html }} style={{ marginTop: '0.5rem', padding: '1rem', background: 'white', borderRadius: '4px' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3 style={{ marginBottom: '0.5rem' }}>4. Send</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '400px' }}>
                <label>
                  Subject: <input type="text" value={newsletterSubject} onChange={(e) => setNewsletterSubject(e.target.value)} placeholder="Newsletter subject" style={{ width: '100%', padding: '0.5rem' }} />
                </label>
                <label>
                  From: <input type="text" value={newsletterFrom} onChange={(e) => setNewsletterFrom(e.target.value)} placeholder="Alert1 Newsletter &lt;newsletter@alert-1.com&gt;" style={{ width: '100%', padding: '0.5rem' }} />
                </label>
                <button type="button" className="btn btn-primary" onClick={handleNewsletterSend} disabled={newsletterSending || !newsletterConfig?.configured}>
                  {newsletterSending ? 'Sending…' : 'Approve and Send'}
                </button>
              </div>
            </div>
            <div>
              <h3 style={{ marginBottom: '0.5rem' }}>Bounce Report</h3>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                After a campaign, bounces are recorded via webhook. Click below to email a CSV of bounces from the last 7 days.
              </p>
              <button type="button" className="btn btn-secondary" onClick={handleNewsletterSendBounceReport} disabled={newsletterBounceReportSending}>
                {newsletterBounceReportSending ? 'Sending…' : 'Send Bounce Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Test Tab Content (Super-User Only) */}
      {activeTab === 'email-test' && isSuperUser && (
        <div className="admin-section">
          <h2>Email Test</h2>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Send a test email to verify email configuration (SMTP or Microsoft Graph). Leave &quot;Send to&quot; blank to use the default recipient.
          </p>
          <div style={{ marginBottom: '1rem', maxWidth: '400px' }}>
            <label htmlFor="email-test-send-to" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Send to (optional)
            </label>
            <input
              id="email-test-send-to"
              type="email"
              value={emailTestSendTo}
              onChange={(e) => setEmailTestSendTo(e.target.value)}
              placeholder="e.g. you@example.com"
              style={{ width: '100%', padding: '0.5rem' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={sendTestEmailRequest}
              disabled={emailTestSending}
            >
              {emailTestSending ? 'Sending…' : 'Send Test Email'}
            </button>
          </div>
          {emailTestResult && (
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '8px',
                background: emailTestResult.success ? '#d4edda' : '#f8d7da',
                color: emailTestResult.success ? '#155724' : '#721c24',
                maxWidth: '500px',
              }}
            >
              <div><strong>Success:</strong> {emailTestResult.success ? 'Yes' : 'No'}</div>
              <div><strong>Config OK:</strong> {emailTestResult.configOk ? 'Yes' : 'No'}</div>
              <div><strong>Send OK:</strong> {emailTestResult.sendOk ? 'Yes' : 'No'}</div>
              {emailTestResult.backend && (
                <div><strong>Backend:</strong> {emailTestResult.backend === 'graph' ? 'Microsoft Graph' : 'SMTP'}</div>
              )}
              {emailTestResult.message && <div style={{ marginTop: '0.5rem' }}>{emailTestResult.message}</div>}
              {emailTestResult.error && <div style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>Error: {emailTestResult.error}</div>}
              {emailTestResult.error && emailTestResult.backend === 'smtp' && /535|Authentication unsuccessful/i.test(emailTestResult.error) && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', opacity: 0.95 }}>
                  <strong>SMTP / Outlook tip:</strong> Use normal password if no 2FA, else App Password. Set <code>SMTP_HOST=smtp.office365.com</code>, full email as <code>SMTP_USER</code>, and ensure SMTP AUTH is enabled. See <code>ORDER_EMAIL_TROUBLESHOOTING.md</code> §5.
                </div>
              )}
              {emailTestResult.error && emailTestResult.backend === 'graph' && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', opacity: 0.95 }}>
                  <strong>Graph tip:</strong> Ensure <code>Mail.Send</code> (and <code>Mail.Send.Shared</code> for shared mailboxes) have admin consent. Confirm <code>AZURE_CLIENT_ID</code>, <code>AZURE_CLIENT_SECRET</code>, <code>AZURE_TENANT_ID</code>, and <code>EMAIL_USERNAME</code> (send-from mailbox) are set. See <code>EMAIL_SETUP.md</code>.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002,
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <h2>My Profile</h2>
            {currentUser && (
              <div style={{ marginBottom: '1.5rem' }}>
                <p><strong>Email:</strong> {currentUser.email}</p>
                {currentUser.fullName && (
                  <p><strong>Full Name:</strong> {currentUser.fullName}</p>
                )}
                {userRole && (
                  <p><strong>Role:</strong> {userRole.charAt(0).toUpperCase() + userRole.slice(1)}</p>
                )}
              </div>
            )}

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Change Password</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label>Current Password *</label>
              <div className="password-input-wrapper">
                <input
                  type={showProfileCurrentPassword ? 'text' : 'password'}
                  value={profileForm.currentPassword}
                  onChange={(e) => setProfileForm({ ...profileForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowProfileCurrentPassword(!showProfileCurrentPassword)}
                  aria-label={showProfileCurrentPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showProfileCurrentPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label>New Password * (min 8 characters)</label>
              <div className="password-input-wrapper">
                <input
                  type={showProfileNewPassword ? 'text' : 'password'}
                  value={profileForm.newPassword}
                  onChange={(e) => setProfileForm({ ...profileForm, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowProfileNewPassword(!showProfileNewPassword)}
                  aria-label={showProfileNewPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showProfileNewPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label>Confirm New Password *</label>
              <div className="password-input-wrapper">
                <input
                  type={showProfileConfirmPassword ? 'text' : 'password'}
                  value={profileForm.confirmPassword}
                  onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowProfileConfirmPassword(!showProfileConfirmPassword)}
                  aria-label={showProfileConfirmPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showProfileConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => {
                  setShowProfileModal(false);
                  setProfileForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setShowProfileCurrentPassword(false);
                  setShowProfileNewPassword(false);
                  setShowProfileConfirmPassword(false);
                }}
                className="btn"
                style={{ background: '#6c757d' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!profileForm.currentPassword || !profileForm.newPassword || !profileForm.confirmPassword) {
                    setError('All password fields are required');
                    return;
                  }

                  if (profileForm.newPassword.length < 8) {
                    setError('New password must be at least 8 characters');
                    return;
                  }

                  if (profileForm.newPassword !== profileForm.confirmPassword) {
                    setError('New passwords do not match');
                    return;
                  }

                  try {
                    const adminEmail = sessionStorage.getItem('adminEmail');
                    const adminPassword = sessionStorage.getItem('adminPassword') || '';

                    const response = await axios.post(`${API_URL}/api/admin-auth/profile/password`, {
                      email: adminEmail,
                      password: adminPassword,
                      currentPassword: profileForm.currentPassword,
                      newPassword: profileForm.newPassword,
                    });

                    if (response.data.success) {
                      setSuccess('Password updated successfully!');
                      setShowProfileModal(false);
                      setProfileForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setShowProfileCurrentPassword(false);
                      setShowProfileNewPassword(false);
                      setShowProfileConfirmPassword(false);
                      // Update stored password
                      sessionStorage.setItem('adminPassword', profileForm.newPassword);
                    }
                  } catch (error: any) {
                    setError(error.response?.data?.error || 'Failed to update password');
                  }
                }}
                className="btn btn-primary"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
